import React from 'react';
import { Document, Packer, Paragraph, ImageRun } from 'docx';
import { Tooltip, IconButton, SvgIconProps } from '@quillbot/design-system';
import createStyles from '@mui/styles/createStyles';
import { makeStyles } from '@mui/styles';
import logger from 'core/logger/logger';
import { useDispatch, useSelector } from 'react-redux';
import { toggleAlertDialogAction } from 'src/_common/actions/commonActions';
import commonTrack from 'src/_common/handlers/track';
import { RootState } from 'src/_common/models/redux/store';
import { DownloadIcon } from 'src/_common/quillElements/icon';
import { APP_SETTINGS } from 'config/constants';
import { useTranslate } from '@Common/hooks';
import AlertDialog from '../systemNotifications/AlertDialog';

interface IProps {
  getText: Function;
  copyAllEvent?: Function;
  htmlToDoc?: boolean; // required for researcher
  downLoadIconProps?: SvgIconProps;
  iconButtonClasses?: object;
  isAsync?: boolean;
  childComp?: JSX.Element | JSX.Element[] | string | null;
  onClick?: Function;
  Icon?: React.FC<any>;
  fileName?: string;
  // isFileUploaded: boolean;
}

const useStyles = makeStyles(() =>
  createStyles({
    btnOutline: {
      padding: '6px 15px',
      marginLeft: '2px',
      minWidth: '25px',
      '&:hover': {
        // background: '#fff',
        background: 'none',
      },
      width: (htmlToDoc) => (htmlToDoc ? '100%' : ''),
      justifyContent: (htmlToDoc) => (htmlToDoc ? 'start' : ''),
    },
  })
);

/**
 * may throw Error, wrap in try...catch
 */
export const createDocFile = ({
  name = `Article_${new Date().toLocaleString()}`,
  images,
  html,
}: {
  name?: string;
  images?: string[];
  html?: string;
}) => {
  let sourceHTML = html;
  const header =
    "<html xmlns:o='urn:schemas-microsoft-com:office:office' " +
    "xmlns:w='urn:schemas-microsoft-com:office:word' " +
    "xmlns='https://www.w3.org/TR/REC-html40'>" +
    "<head><meta charset='utf-8'><title>Exported Article from QuillBot</title></head><body>";
  const footer = '</body></html>';
  images?.forEach?.((image) => {
    sourceHTML += `<br /><img src=${image} width='400px' height='400px' /><br />`;
  });
  const finalHTML = header + sourceHTML + footer;
  const blob = new Blob(['\ufeff', finalHTML], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
  const source = `data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;charset=utf-8,${encodeURIComponent(
    finalHTML
  )}`;
  const filename = `${name}.doc`;
  if (navigator.msSaveOrOpenBlob) {
    navigator.msSaveOrOpenBlob(blob, filename);
  } else {
    // download link
    const fileDownload = document.createElement('a');
    document.body.appendChild(fileDownload);
    fileDownload.href = source;
    fileDownload.download = filename;
    fileDownload.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      })
    );
    document.body.removeChild(fileDownload);
  }
};
export default function FileExporter({
  getText,
  copyAllEvent,
  htmlToDoc,
  iconButtonClasses,
  downLoadIconProps,
  isAsync,
  childComp,
  onClick,
  Icon,
  fileName,
}: IProps) {
  console.log('projectName :===', fileName);

  const classes = useStyles(htmlToDoc);
  const dispatch = useDispatch();
  const { translate } = useTranslate();
  const exportedImages = useSelector((state: RootState) => state.common?.exportedImages);
  const documentName = fileName || `Article_${new Date().toLocaleString()}`;
  const exportFile = () => {
    const paragraphs = getText().split('\n');
    const images = [
      ...exportedImages.map(
        (image) =>
          new Paragraph({
            children: [new ImageRun({ data: image, transformation: { height: 100, width: 200 } })],
          })
      ),
    ];

    const children: Paragraph[] = [
      ...paragraphs.map((paragraph: string) => new Paragraph(paragraph)),
      ...images,
    ];
    const doc = new Document({ sections: [{ children }] });

    Packer.toBlob(doc)
      .then((blob) => {
        commonTrack.fileExported(true);
        if (copyAllEvent) copyAllEvent();
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          return window.navigator.msSaveOrOpenBlob(blob);
        }
        const data = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = data;
        link.download = `Result_${new Date().toLocaleString()}.docx`;

        // this is necessary as link.click() does not work on the latest firefox
        link.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
          })
        );

        setTimeout(() => {
          // For Firefox it is necessary to delay revoking the ObjectURL
          window.URL.revokeObjectURL(data);
          link.remove();
        }, 100);
      })
      .catch((e) => {
        commonTrack.fileExported(false);
        dispatch(
          toggleAlertDialogAction(
            false,
            'Your file failed to download. Please make sure you’re connected to the internet and try again.',
            'Download Failed',
            true,
            undefined,
            true,
            'downloadFile'
          )
        );
        logger('Error in exporting file', e);
      });
    // }
  };

  const htmlToDocFile = async (e: React.MouseEvent) => {
    e.stopPropagation();
    // ! USING `await` with a non-async function like this `async function add(1, 2)` is okay but we are not changing here though
    const text = isAsync ? await getText() : getText();

    try {
      createDocFile({ html: text, name: documentName, images: exportedImages });
      commonTrack.fileExported(true);
      if (copyAllEvent) copyAllEvent();
    } catch (error: any) {
      commonTrack.fileExported(false);
      dispatch(
        toggleAlertDialogAction(
          false,
          'Your file failed to download. Please make sure you’re connected to the internet and try again.',
          'Upload Failed',
          true,
          undefined,
          true,
          'downloadFile'
        )
      );
      logger('Error in exporting file', error);
    }
  };
  return (
    <>
      <AlertDialog />
      <Tooltip arrow title={translate(APP_SETTINGS.showToolTips && !childComp ? 'Export' : '')}>
        <IconButton
          classes={{ root: classes.btnOutline, ...iconButtonClasses }}
          onClick={
            htmlToDoc
              ? (e) => {
                  onClick?.();
                  htmlToDocFile(e);
                }
              : () => {
                  exportFile();
                  onClick?.();
                }
          }
          size="large"
        >
          <span>
            {Icon ? (
              <Icon style={{ marginTop: '8px' }} {...downLoadIconProps} />
            ) : (
              <DownloadIcon style={{ marginTop: '8px' }} {...downLoadIconProps} />
            )}
          </span>
          {childComp}
        </IconButton>
      </Tooltip>
    </>
  );
}
